import { FormProvider, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { ErrorAlert } from "@/lib/components/error-alert";
import { BackButton, BaseNavigationBar } from "@/lib/components/navigation";
import { FormActionButtons, NavigationGuardProvider } from "@/lib/components/navigation-guard";
import { PageContent } from "@/lib/components/page-content";
import { useBusinessSettings } from "../../hooks/use-business-settings";
import { SettingsSearchTrigger } from "../../search/components/settings-search-trigger";
import { BusinessProfileForm } from "./components/business-profile-form";
import { CurrencyTimezoneInfoCard } from "./components/currency-timezone-info-card";
import { OpeningHoursForm } from "./components/opening-hours-form";

export function BusinessSettingsPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useTranslation("settings");

  const { form, loading, saving, error, save, discard } = useBusinessSettings();
  // Subscribe to isDirty via useFormState so React Compiler doesn't memoize
  // away the Proxy access on `form.formState.isDirty`.
  const { isDirty } = useFormState({ control: form.control });

  return (
    <NavigationGuardProvider
      hasUnsavedChanges={isDirty}
      onSave={save}
      onDiscard={discard}
      saving={saving}
      description={t("messages.unsavedChanges")}
    >
      <BaseNavigationBar
        title={t("sections.business.title")}
        subtitle={t("sections.business.description")}
        leadElement={
          isMobile ? <BackButton key="back-button" onBack={() => navigate(-1)} /> : undefined
        }
        trailingElements={[
          <SettingsSearchTrigger key="search-trigger" />,
          <FormActionButtons
            key="form-actions"
            isDirty={isDirty}
            onSave={save}
            onDiscard={discard}
            saving={saving}
          />,
        ]}
      />

      <PageContent>
        <FormProvider {...form}>
          <div className="space-y-6">
            <ErrorAlert error={error} />

            {!error && (
              <>
                <BusinessProfileForm loading={loading} saving={saving} />
                <div id="field-business-currency-timezone">
                  <CurrencyTimezoneInfoCard loading={loading} />
                </div>
                <div id="field-business-operating-hours">
                  <OpeningHoursForm loading={loading} saving={saving} />
                </div>
              </>
            )}
          </div>
        </FormProvider>
      </PageContent>
    </NavigationGuardProvider>
  );
}
